<template>
    <!-- 沿用权限管理的分组布局，支持超级管理员管理账户 MFA。 -->
    <div class="customer-group-list">
        <div class="authority-section" v-if="groupList.length">
            <div class="authority-table" v-for="group in groupList" :key="group.id">
                <div class="authority-table-group">
                    <div class="group-heading"><i class="el-icon-folder-opened"></i><span>{{$t('authority.group_name')}}：{{group.name || '-'}}</span></div>
                    <span class="group-count">{{$t('authority.group_leader')}} {{group.accountList.filter(account => account.role === 101).length}} · {{$t('authority.team_member')}} {{group.accountList.filter(account => account.role === 102).length}}</span>
                </div>
                <div class="authority-table-section">
                    <div class="authority-table-class">
                        <span>{{$t('authority.name')}}</span>
                        <span>{{$t('authority.phone')}}</span>
                        <span>{{$t('user.email')}}</span>
                        <span>{{$t('authority.identity')}}</span>
                        <span>{{$t('authority.state')}}</span>
                        <span>{{$t('authority.create_time')}}</span>
                        <span>{{$t('setting.microsoft_authenticator')}}</span>
                    </div>
                    <div class="authority-table-child" :class="{'authority-table-leader': account.role === 101}" v-for="account in group.accountList" :key="account.id">
                        <span :title="account.username">{{account.username || '-'}}</span>
                        <span>{{account.phone || '-'}}</span>
                        <span :title="account.email">{{account.email || '-'}}</span>
                        <span><span class="role-tag" :class="{'role-leader': account.role === 101}">{{account.role === 101 ? $t('authority.group_leader') : $t('authority.team_member')}}</span></span>
                        <span><span class="state-tag" :class="{'state-enabled': account.is_enable === 0}">{{account.is_enable === 0 ? $t('authority.start') : $t('authority.disables')}}</span></span>
                        <span>{{account.create_time | timeFormat(2)}}</span>
                        <span>
                            <el-switch v-if="canManageMfa" :value="account.is_mfa_enable === 1" :disabled="account.mfaPending === true" active-color="#13ce66" inactive-color="#ff4949" @change="changeMfa(account, $event)"></el-switch>
                            <template v-else>{{account.is_mfa_enable === 1 ? $t('setting.open') : $t('setting.close')}}</template>
                        </span>
                    </div>
                    <div v-if="!group.accountList.length" class="group-empty">暂无组长或组员</div>
                </div>
            </div>
        </div>
        <div v-else class="authority-empty">{{$t('authority.no_group')}}</div>
    </div>
</template>

<script>
    import Core from "core/core";

    export default {
        props: {
            accountList: {
                type: Array,
                default: () => []
            }
        },
        computed: {
            /** 客户详情中的 MFA 开关仅对超级管理员开放，后端同步校验权限。 */
            canManageMfa() {
                const user = Core.Data.getUser();
                return !!user && user.role === 1;
            },
            /** 将批量查询结果按分组归并，保留没有账户的有效分组。 */
            groupList() {
                const groupList = [];
                const groupMap = new Map();
                this.accountList.forEach(account => {
                    let group = groupMap.get(account.group_id);
                    if (!group) {
                        group = {id: account.group_id, name: account.group_name, accountList: []};
                        groupMap.set(account.group_id, group);
                        groupList.push(group);
                    }
                    if (account.id) {
                        group.accountList.push(account);
                    }
                });
                return groupList;
            }
        },
        methods: {
            /** 复用原 MFA 接口，保存成功后更新状态，失败时保留原状态。 */
            changeMfa(account, enable) {
                if (!this.canManageMfa || !account || !account.id || account.mfaPending || (account.role !== 101 && account.role !== 102)) {
                    return;
                }
                this.$set(account, 'mfaPending', true);
                Core.Api.User.mfaEnable(account.id, enable ? 1 : 0).then(() => {
                    this.$set(account, 'is_mfa_enable', enable ? 1 : 0);
                    this.$message.success(enable ? '已开启 Microsoft Authenticator' : '已关闭 Microsoft Authenticator');
                }).catch(error => {
                    console.error('切换 MFA 状态失败', error);
                    this.$message.error('MFA 状态修改失败，请稍后重试');
                }).finally(() => {
                    this.$set(account, 'mfaPending', false);
                });
            }
        }
    };
</script>

<style scoped lang="scss">
    .customer-group-list {
        max-height: calc(80vh - 150px);
        min-height: 180px;
        overflow: auto;
        padding: 20px 24px;
        background: #F5F7FA;
        box-sizing: border-box;
        .authority-section {
            min-width: 1060px;
        }
        .authority-table {
            overflow: hidden;
            background: #FFFFFF;
            color: #363D42;
            margin-bottom: 16px;
            border: 1px solid #E7ECF2;
            border-radius: 8px;
            &:last-child { margin-bottom: 0; }
        }
        .authority-table-group {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 20px;
            border-bottom: 1px solid #ECF0F4;
            padding: 14px 18px;
        }
        .group-heading {
            display: flex;
            align-items: center;
            min-width: 0;
            font-size: 15px;
            font-weight: 600;
            overflow-wrap: anywhere;
            i { margin-right: 8px; color: #2B6CDA; }
        }
        .group-count {
            flex-shrink: 0;
            color: #8592A3;
            font-size: 12px;
        }
        .authority-table-class, .authority-table-child {
            display: grid;
            grid-template-columns: minmax(100px, 1fr) 130px minmax(190px, 1.6fr) 76px 76px 152px 180px;
            align-items: center;
            padding: 0 18px;
            font-size: 13px;
            >span {
                min-width: 0;
                padding: 12px 8px;
                box-sizing: border-box;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
            >span:nth-child(4), >span:nth-child(5), >span:nth-child(7) { text-align: center; }
        }
        .authority-table-class {
            background: #F8FAFC;
            color: #788597;
            font-size: 12px;
            border-bottom: 1px solid #EEF1F5;
        }
        .authority-table-child {
            min-height: 52px;
            border-bottom: 1px solid #F0F3F7;
            &:last-child { border-bottom: none; }
            &:hover { background: #F5F9FF; }
        }
        .authority-table-leader { background: #FAFCFF; }
        .role-tag, .state-tag {
            display: inline-block;
            padding: 3px 9px;
            border-radius: 4px;
            line-height: 18px;
            font-size: 12px;
        }
        .role-tag { background: #F0F3F7; color: #64748B; }
        .role-leader { background: #EAF2FF; color: #2B6CDA; }
        .state-tag { background: #F1F3F5; color: #9098A4; }
        .state-enabled { background: #EAF8F0; color: #26A66A; }
        .authority-empty, .group-empty {
            padding: 30px;
            text-align: center;
            color: #9098A4;
        }
        @media (max-width: 768px) {
            padding: 12px;
        }
    }
</style>
